/* ==========================================================================
   kbd-motion.js - squarespace-look motion layer (docs/squarespace-look-spec.md
   section 4), wave 1, 2026-09-08.

   MAIN SITE ONLY. layout/theme.liquid loads this file inside `unless bare`,
   so no landing template ever executes it.

   Owns: scroll reveals (spec 3.12), rails (3.6), the header scroll state and
   the header offset class on #swup (3.13), and re-init after every Swup soft
   navigation. Button arrows, the accordion, both drawers and the mega menu
   need nothing from this file - theme.js keeps driving those.

   Contract: window.kbdMotion = { init: function (root) } - idempotent per
   element via data-kbd-motion-init. Nothing else is global.

   HOOKS: `data-kbd-reveal` / `data-kbd-reveal="group"` (reveal units get
   .is-in once); `[data-kbd-rail]` on a .kbd-ui-rail scroller whose controls
   live in the next sibling `.kbd-ui-rail__nav` (or any
   `.kbd-ui-rail__nav[data-kbd-rail-for="<rail id>"]`); `.header` gets
   .is-scrolled past 24px and .is-over-hero while #swup holds
   [data-page="home"]; #swup gets .kbd-ui-header-offset on every other page.

   HANDLED FAILURES
   - No IntersectionObserver: every reveal unit gets .is-in at init.
   - No Swup (theme editor / script missing) or window.kbdSwup not exposed:
     DOMContentLoaded init plus a MutationObserver on <body> that re-inits
     when the #swup element is replaced (Swup 4 swaps the element itself).
   - Rail without nav / dots / buttons: it still scrolls and snaps; only the
     missing controls go unwired.
   - scrollTo({ behavior }) unsupported (older Safari throws on the options
     object): falls back to assigning scrollLeft.
   - matchMedia missing: reduced motion is treated as off.
   - prefers-reduced-motion: reveals get .is-in immediately; rails scroll
     with behavior 'auto'.
   NOT HANDLED (by choice): rails whose items change after init (nothing on
   the site re-renders a rail today - call kbdMotion.init on the new markup
   after removing data-kbd-motion-init if that ever changes).

   ASCII straight quotes only. No libraries. `node --check` clean.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var html = doc.documentElement;
  var win = window;

  // The hidden reveal state in kbd-ui.css is gated on html.kbd-ui-js, so a
  // page whose JS never ran (or failed) shows everything. Added first thing.
  html.classList.add('kbd-ui-js');

  var INIT_ATTR = 'data-kbd-motion-init';
  var SCROLL_THRESHOLD = 24;

  var reduceMotion = false;
  try {
    reduceMotion = !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) {
    reduceMotion = false;
  }

  var hasIO = typeof win.IntersectionObserver === 'function';

  function isInited(el) { return el.getAttribute(INIT_ATTR) === '1'; }
  function markInited(el) { el.setAttribute(INIT_ATTR, '1'); }
  function toArray(list) { return Array.prototype.slice.call(list || []); }

  // querySelectorAll never matches the root itself, so an init(rootEl) on an
  // element that IS a reveal unit / rail has to be picked up by hand.
  function collect(root, selector, attr) {
    var els = toArray(root.querySelectorAll(selector));
    if (root !== doc && root.nodeType === 1 && root.hasAttribute(attr)) els.unshift(root);
    return els;
  }

  /* ---------------------------------------------------------------- reveals
     Spec 3.12. Units below the fold are observed and get .is-in once when
     they cross the bottom 12% of the viewport. Units already in view at init
     get .is-in in the SAME task as the html.kbd-ui-js gate, so their hidden
     state never reaches a paint: no flicker on slow loads, no .7s wait for
     above-the-fold content. (The spec's "next frame" would paint one hidden
     frame first on a slow first load.) */
  var revealObserver = null;

  function reveal(el) { el.classList.add('is-in'); }

  function getRevealObserver() {
    if (revealObserver || !hasIO) return revealObserver;
    revealObserver = new win.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          reveal(entries[i].target);
          revealObserver.unobserve(entries[i].target);
        }
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0 });
    return revealObserver;
  }

  // Group children stagger by --kbd-ui-i (kbd-ui.css multiplies it by
  // --kbd-ui-stagger). A hand-set value on a child is respected.
  function numberGroupChildren(group) {
    var kids = group.children;
    for (var i = 0; i < kids.length; i++) {
      if (!kids[i].style.getPropertyValue('--kbd-ui-i')) {
        kids[i].style.setProperty('--kbd-ui-i', String(i));
      }
    }
  }

  function inViewportNow(el) {
    var r = el.getBoundingClientRect();
    var vh = win.innerHeight || html.clientHeight || 0;
    return r.bottom > 0 && r.top < vh * 0.88;
  }

  function initReveals(root) {
    var els = collect(root, '[data-kbd-reveal]', 'data-kbd-reveal');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (isInited(el)) continue;
      markInited(el);
      if (el.getAttribute('data-kbd-reveal') === 'group') numberGroupChildren(el);
      if (reduceMotion || !hasIO || inViewportNow(el)) {
        reveal(el);
      } else {
        getRevealObserver().observe(el);
      }
    }
  }

  /* ------------------------------------------------------------------ rails
     Spec 3.6. The rail is a CSS scroll-snap scroller; this only wires the
     controls. Active index comes from the scroll position (nearest snap
     start), which is what the dots should reflect - an IntersectionObserver
     on the items cannot tell "leftmost of four visible cards" apart from the
     other three, so the spec's IO suggestion is not used here. */
  function findRailNav(rail) {
    var nav = null;
    if (rail.id) {
      nav = doc.querySelector('.kbd-ui-rail__nav[data-kbd-rail-for="' + rail.id + '"]');
    }
    if (!nav) {
      var sib = rail.nextElementSibling;
      if (sib && sib.classList.contains('kbd-ui-rail__nav')) nav = sib;
    }
    return nav;
  }

  function railScrollTo(rail, left) {
    var behavior = reduceMotion ? 'auto' : 'smooth';
    try {
      rail.scrollTo({ left: left, behavior: behavior });
    } catch (e) {
      rail.scrollLeft = left;
    }
  }

  function initRail(rail) {
    if (isInited(rail)) return;
    markInited(rail);
    var items = toArray(rail.children);
    if (!items.length) return;

    var nav = findRailNav(rail);
    var prev = nav ? nav.querySelector('.kbd-ui-rail__btn--prev') : null;
    var next = nav ? nav.querySelector('.kbd-ui-rail__btn--next') : null;
    var dotsWrap = nav ? nav.querySelector('.kbd-ui-rail__dots') : null;
    var dots = [];
    var active = -1;

    function padLeft() {
      var v = parseFloat(win.getComputedStyle(rail).paddingLeft);
      return isNaN(v) ? 0 : v;
    }
    // .kbd-ui-rail is position:relative (kbd-ui.css), so offsetLeft is
    // measured from the rail's own padding edge.
    function targetLeft(i) { return items[i].offsetLeft - padLeft(); }
    function nearestIndex() {
      var x = rail.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < items.length; i++) {
        var d = Math.abs(targetLeft(i) - x);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }
    function atEnd() { return rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1; }
    function goTo(i) {
      i = Math.max(0, Math.min(items.length - 1, i));
      railScrollTo(rail, targetLeft(i));
    }
    function update() {
      var i = nearestIndex();
      if (i !== active) {
        active = i;
        for (var d = 0; d < dots.length; d++) {
          dots[d].classList.toggle('is-active', d === i);
          dots[d].setAttribute('aria-current', d === i ? 'true' : 'false');
        }
      }
      if (prev) prev.disabled = rail.scrollLeft <= 1;
      if (next) next.disabled = atEnd();
    }

    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      items.forEach(function (item, i) {
        var b = doc.createElement('button');
        b.type = 'button';
        b.className = 'kbd-ui-rail__dot';
        b.setAttribute('aria-label', 'Go to item ' + (i + 1) + ' of ' + items.length);
        b.addEventListener('click', function () { goTo(i); });
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }
    if (prev) prev.addEventListener('click', function () { goTo(nearestIndex() - 1); });
    if (next) next.addEventListener('click', function () { goTo(nearestIndex() + 1); });

    if (!rail.hasAttribute('tabindex')) rail.setAttribute('tabindex', '0');
    rail.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(nearestIndex() + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(nearestIndex() - 1); }
    });

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      win.requestAnimationFrame(function () { ticking = false; update(); });
    }
    rail.addEventListener('scroll', onScroll, { passive: true });
    win.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function initRails(root) {
    var rails = collect(root, '[data-kbd-rail]', 'data-kbd-rail');
    for (var i = 0; i < rails.length; i++) initRail(rails[i]);
  }

  /* ----------------------------------------------------------------- header
     Spec 3.13. theme.js initNavTransparency() strips nav--transparent /
     nav--scrolled / nav--solid, so these are deliberately different names. */
  var headerEl = null;
  function getHeader() {
    if (!headerEl) headerEl = doc.querySelector('.header');
    return headerEl;
  }

  /* Oura hides the header while you scroll DOWN and returns it the moment you
     scroll up (measured: top 0 -> -112px and back). HIDE_AFTER keeps it put
     over the hero, and HIDE_DELTA ignores the small jitter a trackpad or a
     focus-scroll produces. .is-hidden is a class swap; the slide itself is the
     CSS transition on `top`, which the reduced-motion block cancels. */
  var lastScrollY = 0;
  var HIDE_AFTER = 240;
  var HIDE_DELTA = 6;

  function syncHeaderScroll() {
    var h = getHeader();
    if (!h) return;
    var y = win.pageYOffset || html.scrollTop || 0;
    h.classList.toggle('is-scrolled', y > SCROLL_THRESHOLD);
    var delta = y - lastScrollY;
    if (Math.abs(delta) > HIDE_DELTA) {
      if (delta > 0 && y > HIDE_AFTER) {
        h.classList.add('is-hidden');
      } else if (delta < 0) {
        h.classList.remove('is-hidden');
      }
      lastScrollY = y;
    }
    if (y <= HIDE_AFTER) h.classList.remove('is-hidden');
  }

  function syncPageContext() {
    var swupEl = doc.getElementById('swup');
    var isHome = !!(swupEl && swupEl.querySelector('[data-page="home"]'));
    var h = getHeader();
    if (h) h.classList.toggle('is-over-hero', isHome);
    if (swupEl) swupEl.classList.toggle('kbd-ui-header-offset', !isHome);
    syncHeaderScroll();
  }

  var scrollTicking = false;
  function onWindowScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    win.requestAnimationFrame(function () { scrollTicking = false; syncHeaderScroll(); });
  }

  /* ------------------------------------------------------------------- init */
  function init(root) {
    if (!root || typeof root.querySelectorAll !== 'function') root = doc;
    initReveals(root);
    initRails(root);
    syncPageContext();
  }

  /* --------------------------------------------------------------- preload
     Swup only starts fetching the next page AFTER the click, so every
     navigation paid a full round trip while the old page sat faded out -
     measured at 1.9s from click to content swap on the dev server, and the
     complaint that "every time a new page is clicked it takes a second".

     Fetching on intent fixes it: the moment a pointer touches an internal
     link, or it receives keyboard focus, the page is fetched into Swup's
     cache, so the later click usually swaps from memory. Each URL is fetched
     at most once, and a failure clears the flag so a retry can happen on the
     next hover. Everything is guarded - a preload must never break a click. */
  var preloadTried = {};

  function preloadableHref(a) {
    if (!a || !a.getAttribute) return null;
    var href = a.getAttribute('href');
    if (!href || href.charAt(0) !== '/') return null;
    // Mirror the linkSelector Swup itself is configured with.
    if (a.hasAttribute('data-no-swup')) return null;
    if (a.getAttribute('target') === '_blank') return null;
    if (href.indexOf('/checkout') === 0 || href.indexOf('/cart') === 0) return null;
    if (href === win.location.pathname) return null;
    return href;
  }

  function preload(href) {
    if (!href || preloadTried[href]) return;
    var swup = win.kbdSwup;
    if (!swup || typeof swup.fetchPage !== 'function') return;
    if (swup.cache && typeof swup.cache.has === 'function' && swup.cache.has(href)) {
      preloadTried[href] = 1;
      return;
    }
    preloadTried[href] = 1;
    try {
      var p = swup.fetchPage(href);
      if (p && typeof p.catch === 'function') {
        p.catch(function () { preloadTried[href] = 0; });
      }
    } catch (e) {
      preloadTried[href] = 0;
    }
  }

  function onPreloadIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var a = t.closest('a');
    if (!a) return;
    preload(preloadableHref(a));
  }

  var preloadBound = false;
  function bindPreload() {
    if (preloadBound || !doc.body) return;
    preloadBound = true;
    // mouseover and focusin bubble, so one delegated listener covers links
    // that arrive later with a swapped page. touchstart is passive so it can
    // never delay a tap.
    doc.body.addEventListener('mouseover', onPreloadIntent);
    doc.body.addEventListener('focusin', onPreloadIntent);
    doc.body.addEventListener('touchstart', onPreloadIntent, { passive: true });
  }

  var navBound = false;
  function bindNavigation() {
    if (navBound) return;
    var swup = win.kbdSwup;
    if (swup && swup.hooks && typeof swup.hooks.on === 'function') {
      navBound = true;
      // content:replace runs once the new #swup is in the DOM and before its
      // first paint, so above-the-fold reveals settle without a hidden frame.
      // page:view is the safety net; init is idempotent so the double call is free.
      swup.hooks.on('content:replace', function () { init(doc); });
      swup.hooks.on('page:view', function () { init(doc); });
      return;
    }
    if (typeof win.MutationObserver === 'function' && doc.body) {
      navBound = true;
      var pending = false;
      var observer = new win.MutationObserver(function () {
        if (pending) return;
        pending = true;
        win.requestAnimationFrame(function () { pending = false; init(doc); });
      });
      observer.observe(doc.body, { childList: true });
    }
  }

  function boot() {
    init(doc);
    bindPreload();
    win.addEventListener('scroll', onWindowScroll, { passive: true });
  }

  // Deferred scripts run before DOMContentLoaded, so boot runs synchronously
  // here on a normal load; the listener covers an early inline placement.
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  // theme.js (deferred, loaded after this file) creates its Swup instance
  // synchronously during its own execution, i.e. before DOMContentLoaded.
  // A zero-delay timer queued from DOMContentLoaded therefore sees
  // window.kbdSwup whenever it exists at all.
  if (doc.readyState === 'complete') {
    setTimeout(bindNavigation, 0);
  } else {
    win.addEventListener('DOMContentLoaded', function () { setTimeout(bindNavigation, 0); });
  }

  win.kbdMotion = { init: init };
})();
